import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../utils/api';
import PasswordStrengthMeter, { validatePasswordFrontend } from '../components/PasswordStrengthMeter';
import '../AuthLayout.css';

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // If no token or email, show invalid link message
  if (!token || !email) {
    return (
      <div className="auth-page">
        <div className="auth-bg" style={{ backgroundImage: 'url(/images/login/260318_DGTL_ChariotLoginArt_C4_VeryCompressed.png)' }} />
        <div className="auth-left">
          <div className="auth-container">
            <div className="auth-header">
              <img src="/images/Branding/Horizontal Logo/Chariot_HorizontalLogo_P1_FullColor_011226.png" alt="Chariot" className="auth-logo" />
              <h1>Invalid Link</h1>
              <p className="auth-subtitle">
                This password reset link is invalid or has expired.
              </p>
            </div>

            <button
              type="button"
              className="auth-button"
              onClick={() => navigate('/forgot-password')}
            >
              Request New Reset Link
            </button>

            <div className="auth-footer" style={{ marginTop: '14px' }}>
              <p>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => navigate('/login')}
                >
                  Back to Sign In
                </button>
              </p>
            </div>

            <div className="auth-company">
              <p>Poseidon Marine</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordErrors = validatePasswordFrontend(password);
    if (passwordErrors.length > 0) {
      setError(passwordErrors[0]);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email, newPassword: password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Password reset failed');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  };

  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-bg" style={{ backgroundImage: 'url(/images/login/260318_DGTL_ChariotLoginArt_C4_VeryCompressed.png)' }} />
        <div className="auth-left">
          <div className="auth-container">
            <div className="auth-header">
              <img src="/images/Branding/Horizontal Logo/Chariot_HorizontalLogo_P1_FullColor_011226.png" alt="Chariot" className="auth-logo" />
              <h1>Password Reset</h1>
              <p className="auth-subtitle">Your password has been updated</p>
            </div>

            <div className="auth-verification">
              <div className="auth-success-icon">&#10003;</div>
              <p>Your password has been successfully reset. You can now sign in with your new password.</p>
            </div>

            <button
              type="button"
              className="auth-button"
              onClick={() => navigate('/login')}
            >
              Sign In
            </button>

            <div className="auth-company">
              <p>Poseidon Marine</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-bg" style={{ backgroundImage: 'url(/images/login/260318_DGTL_ChariotLoginArt_C4_VeryCompressed.png)' }} />
      <div className="auth-left">
        <div className="auth-container">
          <div className="auth-header">
            <img src="/images/Branding/Horizontal Logo/Chariot_HorizontalLogo_P1_FullColor_011226.png" alt="Chariot" className="auth-logo" />
            <h1>Set New Password</h1>
            <p className="auth-subtitle">
              Create a new password for your account
            </p>
          </div>

          {error && (
            <div className="auth-error">{error}</div>
          )}

          <form className="auth-form" data-autofill="allow" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 12 characters"
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              <PasswordStrengthMeter password={password} />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="auth-button"
              disabled={loading}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>

            <div className="auth-footer">
              <p>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => navigate('/login')}
                >
                  Back to Sign In
                </button>
              </p>
            </div>
          </form>

          <div className="auth-company">
            <p>Poseidon Marine</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;
